import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form'
// components
import { ReduxSwitch } from 'components/FormElements/ReduxForm'
// view components
import MainInfo from './components/MainInfo'
import FormFields from './components/FormFields'
import DateSettings from './components/DateSettings'
import CustomOptions from './components/CustomOptions'
import LookupSettings from './components/LookupSettings'
import NumberSettings from './components/NumberSettings'
import FloatingActions from 'components/FormElements/FloatingActions'
// views
import { Chip, ChipContainer, InnerWrapper, ScrollContainer, SwitchContainer } from './view'
// constants
import { Forms } from 'constants/index'
import { NO_VALUE_FIELD_TYPES } from 'constants/objects'
// hooks
import { useFieldType, useFormDirtyCount } from 'hooks/index'
// types
import { IField, IFieldOption } from 'types/graphql'

interface IIncomingProps {
  property: IField
  isReviewField?: boolean
  dirtyFieldsCount?: number
}

interface IFormData {
  active: boolean
  required?: boolean
  restricted?: boolean
  readonly?: boolean
  hidden?: boolean
  exportHeaders?: boolean
  includeHistoryInApiResponse?: boolean
  hideOnPublic?: boolean
  isPublicOnly?: boolean
  showUserDetails?: boolean
  visibleUserDetailsFields?: string[]
  historyEnabled?: boolean
  displayEmail?: boolean
  wysiwyg?: boolean
  searchable?: boolean
  parentFieldId?: IFieldOption
  hideName?: boolean
  name: string
  settings?: {
    format?: IFieldOption
    dateFormat?: string
    manualSave?: boolean
    minRange?: string
    maxRange?: string
    appetiteEnabled?: boolean
  }
  multiple?: boolean
  responseType?: boolean
  responseTypeId?: IFieldOption
  weight?: string
  multiline?: boolean
  format?: IFieldOption
  source?: IFieldOption[]
  objectSecurity?: IFieldOption
  backgroundColor?: string
  includeInAiContext?: boolean
  aiAutoPopulate?: boolean
  aiIncludeHistory?: boolean
  aiContextDescription?: string
  aiSuggestedPrompts?: string[]
  // disabled
  object: string
  fieldId: string
  grouping: string
  fieldType: string
  globalPropertyName?: string
}

type IFormProps = InjectedFormProps<IFormData, IIncomingProps>

type IProps = IIncomingProps & IFormProps & { dirtyFieldsCount: number }

const selector = formValueSelector(Forms.PROPERTY_EDIT_FORM)

const PropertyDetailsForm: React.FC<IProps> = ({ handleSubmit, property, reset, submitting, isReviewField }) => {
  const router = useRouter()

  // state (not a ref) so the options list re-renders once the scroll container is mounted
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement>(null)

  const { dirtyFieldsCount } = useFormDirtyCount(Forms.PROPERTY_EDIT_FORM)

  const responseType = useSelector((state) => selector(state, 'responseType') || false)

  const { isText, isDate, isNumber, isPickList, isLookup } = useFieldType(property)

  const isForm = router.pathname.includes('forms')
  const showMandatory = !NO_VALUE_FIELD_TYPES.includes(property.fieldType)
  const canEditOptions = property.name !== 'resultInputType' && property.name !== 'inputMethod'

  return (
    <ScrollContainer ref={setScrollParent}>
      <form onSubmit={handleSubmit}>
        <InnerWrapper isFormDirty={!!dirtyFieldsCount}>
          <SwitchContainer>
            <ReduxSwitch name='active' disabled={property.standard} label='Property Active' reverse />
            <ChipContainer>
              {property.standard && <Chip label='STANDARD' />}
              {(property.parentFieldId || !property.entityId) && <Chip label='GLOBAL' />}
            </ChipContainer>
          </SwitchContainer>
          {showMandatory && !isReviewField && <ReduxSwitch name='required' label='Mandatory' reverse />}
          {isReviewField && <ReduxSwitch name='readonly' label='Read Only' reverse />}
          {!property.standard && !isForm && !isReviewField && (
            <ReduxSwitch name='restricted' label='Restricted' reverse />
          )}

          {isText && <ReduxSwitch name='multiline' disabled={isForm} checked label='Allow Multiline' reverse />}

          {property.name === 'resourceDescription' && <ReduxSwitch name='wysiwyg' label='Display as WYSIWYG' reverse />}

          {isForm && <FormFields property={property} />}
          {!isForm && <MainInfo property={property} />}

          {isDate && <DateSettings />}
          {isNumber && <NumberSettings isForm={isForm} />}
          {isLookup && <LookupSettings isForm={isForm} property={property} />}

          {isPickList && !responseType && (
            <>
              <ReduxSwitch name='multiple' label='Allow Multiple Selections' reverse />
              <CustomOptions property={property} isDisabled={!canEditOptions} scrollParent={scrollParent} />
            </>
          )}
        </InnerWrapper>
        <FloatingActions submitting={submitting} onCancel={reset} dirtyCount={dirtyFieldsCount} />
      </form>
    </ScrollContainer>
  )
}

export default reduxForm<IFormData, IIncomingProps>({ form: Forms.PROPERTY_EDIT_FORM, enableReinitialize: true })(
  PropertyDetailsForm
)
